/**
 * IdleWarningModal — shown during the idle-timeout warning window.
 *
 * Per docs/clearpath-architecture/11-sessions-and-tokens.md §"Idle timeout".
 * Consumers mount this alongside <IdleTimeoutProvider> (from
 * @clearpath/auth-client) and it reads the current warning state + countdown
 * from context, so there is no prop plumbing at the call site.
 *
 * Design choices:
 *   - Self-contained CSS (injected via a <style> block with a unique
 *     class-prefix). Consumers don't need to import any stylesheet; this
 *     keeps the component droppable into apps/main (Tailwind),
 *     apps/vendor / apps/admin / apps/account (plain CSS), and apps/portal
 *     (design-system) without collision.
 *   - role="alertdialog" (per brief) — screen readers announce aggressively.
 *   - aria-live="assertive" on the countdown so updates are spoken.
 *   - Escape key = "stay signed in" (less destructive default).
 *   - Focus trap + focus restore (matches StepUpPromptModal pattern).
 *   - Silent refresh continues in the background — the modal does not block
 *     the scheduler. "Stay signed in" additionally triggers an explicit
 *     silent refresh via the provider's `onStay` hook so the token is
 *     maximally young when the user comes back.
 */
import type { JSX } from 'react';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { useIdleTimeoutContext } from "@clearpath/auth-client";
import type { BrandConfig } from "./types.js";

export interface IdleWarningModalProps {
  /** Optional brand override. primaryColor drives the CTA accent. */
  brand?: BrandConfig;
  /** Optional override for the product noun in copy ("ClearPath"). */
  productLabel?: string;
  /** Override the primary button colour (takes precedence over brand). */
  primaryColor?: string;
}

const FOCUSABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

// Scoped class prefix to avoid collisions with host app styles.
const PREFIX = "cp-idle-modal";

const INLINE_CSS = `
.${PREFIX}-backdrop{position:fixed;inset:0;background:rgba(11,15,26,.55);display:flex;align-items:center;justify-content:center;z-index:10000;padding:1rem;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;}
.${PREFIX}-dialog{background:#fff;color:#0b0f1a;border-radius:.75rem;box-shadow:0 20px 40px rgba(11,15,26,.25);padding:1.5rem;width:100%;max-width:22rem;display:flex;flex-direction:column;gap:.75rem;outline:none;}
@media (prefers-color-scheme: dark){
  .${PREFIX}-dialog{background:#13151d;color:#f5f5f7;box-shadow:0 20px 40px rgba(0,0,0,.55);}
}
.${PREFIX}-title{margin:0;font-size:1.125rem;font-weight:600;}
.${PREFIX}-body{margin:0;font-size:.9375rem;line-height:1.4;}
.${PREFIX}-muted{margin:0;opacity:.65;font-size:.8125rem;}
.${PREFIX}-actions{display:flex;gap:.5rem;justify-content:flex-end;margin-top:.25rem;}
.${PREFIX}-btn{font:inherit;font-size:.875rem;padding:.5rem 1rem;border-radius:.5rem;border:1px solid transparent;cursor:pointer;line-height:1;}
.${PREFIX}-btn:focus-visible{outline:2px solid var(--${PREFIX}-primary,#5b5bd6);outline-offset:2px;}
.${PREFIX}-btn--ghost{background:transparent;border-color:currentColor;color:inherit;opacity:.8;}
.${PREFIX}-btn--ghost:hover{opacity:1;}
.${PREFIX}-btn--primary{background:var(--${PREFIX}-primary,#5b5bd6);color:#fff;}
.${PREFIX}-btn--primary:hover{filter:brightness(1.05);}
`;

export function IdleWarningModal({
  brand,
  productLabel,
  primaryColor,
}: IdleWarningModalProps): JSX.Element | null {
  const { warning, stayActive, signOutNow, getLogoutAtMs, warningMinutes } =
    useIdleTimeoutContext();

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<Element | null>(null);

  const [secondsLeft, setSecondsLeft] = useState<number>(
    Math.max(0, Math.floor((getLogoutAtMs() - Date.now()) / 1000)),
  );

  useEffect(() => {
    if (!warning) return;
    openerRef.current =
      typeof document !== "undefined" ? document.activeElement : null;

    setSecondsLeft(Math.max(0, Math.floor((getLogoutAtMs() - Date.now()) / 1000)));

    const tick = setInterval(() => {
      const remaining = Math.max(
        0,
        Math.floor((getLogoutAtMs() - Date.now()) / 1000),
      );
      setSecondsLeft(remaining);
    }, 1000);

    const node = dialogRef.current;
    if (node) {
      const first = node.querySelector<HTMLElement>('button[data-primary="true"]');
      (first ?? node).focus();
    }

    return () => {
      clearInterval(tick);
      const prev = openerRef.current;
      if (prev instanceof HTMLElement) prev.focus();
    };
  }, [warning, getLogoutAtMs]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      e.stopPropagation();
      stayActive();
      return;
    }
    if (e.key !== "Tab" || !dialogRef.current) return;
    const focusables = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
    ).filter((el) => !el.hasAttribute("disabled"));
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const style = useMemo<CSSProperties | undefined>(() => {
    const color = primaryColor ?? brand?.primaryColor;
    if (!color) return undefined;
    return { [`--${PREFIX}-primary` as string]: color } as CSSProperties;
  }, [primaryColor, brand]);

  if (!warning) return null;

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const countdownText =
    mins > 0 ? `${mins}m ${secs.toString().padStart(2, "0")}s` : `${secs}s`;

  const product = productLabel ?? brand?.productLabel ?? "this session";

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: INLINE_CSS }} />
      <div
        className={`${PREFIX}-backdrop`}
        role="presentation"
        onClick={stayActive}
      >
        <div
          ref={dialogRef}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={`${PREFIX}-title`}
          aria-describedby={`${PREFIX}-body`}
          tabIndex={-1}
          className={`${PREFIX}-dialog`}
          style={style}
          onKeyDown={onKeyDown}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 id={`${PREFIX}-title`} className={`${PREFIX}-title`}>
            Still there?
          </h2>
          <p id={`${PREFIX}-body`} className={`${PREFIX}-body`}>
            You'll be signed out of {product} in{" "}
            <strong aria-live="assertive" aria-atomic="true">
              {countdownText}
            </strong>{" "}
            because of inactivity.
          </p>
          <p className={`${PREFIX}-muted`}>
            Warning window: {warningMinutes} minute{warningMinutes === 1 ? "" : "s"}.
          </p>
          <div className={`${PREFIX}-actions`}>
            <button
              type="button"
              className={`${PREFIX}-btn ${PREFIX}-btn--ghost`}
              onClick={signOutNow}
            >
              Sign out now
            </button>
            <button
              type="button"
              data-primary="true"
              className={`${PREFIX}-btn ${PREFIX}-btn--primary`}
              onClick={stayActive}
            >
              Stay signed in
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
